import type { JSX, ReactNode } from 'react';
import { useId } from 'react';
import styles from './Field.module.css';

/**
 * Обычные подписанные поля. Ничего самодельного: label связан с control,
 * пояснение и ошибка привязаны через aria-describedby, сообщения текстовые.
 */

export type FieldProps = {
  label: string;
  hint?: ReactNode;
  error?: string;
  /**
   * Идентификатор блока, где сообщения об ошибках показаны отдельно.
   * Нужен, чтобы не дублировать один и тот же текст рядом с полем и в общем
   * списке: скринридер всё равно дойдёт до него через aria-describedby.
   */
  messagesId?: string;
  children: (props: {
    id: string;
    describedBy: string | undefined;
  }) => ReactNode;
};

export function Field({ label, hint, error, messagesId, children }: FieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null, messagesId ?? null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy: describedBy === '' ? undefined : describedBy })}
      {hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className={styles.error} id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export type FieldsetProps = {
  legend: string;
  hint?: ReactNode;
  children: ReactNode;
};

export function Fieldset({ legend, hint, children }: FieldsetProps): JSX.Element {
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{legend}</legend>
      {hint ? <p className={styles.hint}>{hint}</p> : null}
      {children}
    </fieldset>
  );
}
